import { MediaMatcher } from '@angular/cdk/layout';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { Router } from '@angular/router';
import { AuthGuard } from '@app/guards/auth.guard';
import { AuthenticationService } from '@app/services/auth.service';
import { SpinnerService } from '@app/services/spinner.service';
import { Roles } from '@app/types';
import { Clinic } from '@features/auth/login/select-user-clinic-dialog/select-user-clinic-dialog.model';
import { SelectUserClinicDialogService } from '@features/auth/login/select-user-clinic-dialog/select-user-clinic-dialog.service';
import { UserType } from '@shared/Constants/commonConstants';
import { Subscription, timer } from 'rxjs';

@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
})
export class LayoutComponent implements OnInit, OnDestroy, AfterViewInit {
  private _mobileQueryListener: () => void;
  mobileQuery: MediaQueryList;
  userName: string = '';
  currentRole: number[] | undefined;
  isAdmin: boolean = false;
  isaAdmin: boolean = false;
  isClinicManager: boolean = false;
  isPharmacist: boolean = false;
  isOpsManager: boolean = false;
  isReceptionist: boolean = false;
  isWhatsAppOperator: boolean = false;

  isPharmacyOperator: boolean = false;
  isPharmacyManager: boolean = false;
  isStockTransfer: boolean = false;
  isAuditor: boolean = false;
  isTaskReviewer: boolean = false;
  isStoreManager: boolean = false;
  isStoreApproval: boolean = false;
  isMaximumDiscountOverride: boolean = false;
  rolesWithoutDoctorAndPatient: number[] = [];
  selectedRole: number;
  currentClinic: Clinic | null;
  masterSubCategoryURLs: { [key: string]: Array<string> };
  private autoLogoutSubscription: Subscription = new Subscription();

  clinicLocations: Clinic[];
  filteredClinicLocations: Clinic[] = [];
  clinicLocationControl = new FormControl();

  @ViewChild(MatSelect) clinicLocationSelect: MatSelect;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private media: MediaMatcher,
    public spinnerService: SpinnerService,
    private authService: AuthenticationService,
    private authGuard: AuthGuard,
    public _router: Router,
    private selectUserClinicService: SelectUserClinicDialogService
  ) {
    this.mobileQuery = this.media.matchMedia('(max-width: 1000px)');
    this._mobileQueryListener = () => changeDetectorRef.detectChanges();
    // tslint:disable-next-line: deprecation
    this.mobileQuery.addListener(this._mobileQueryListener);
  }

  ngOnInit(): void {
    this.isPharmacyManager = this.authService.isPharmacyManager();
    this.getClinicLocations();
    this.isClinicManager = this.authService.isClinicManager();
    this.isAdmin = this.authService.isAdmin();
    this.isWhatsAppOperator = this.authService.isWhatsAppOperator();
    this.isOpsManager = this.authService.isOpsManager();
    this.isReceptionist = this.authService.isReceptionist();
    this.isPharmacyOperator = this.authService.isPharmacyOperator();
    this.isPharmacist = this.authService.isPharmacist();
    this.isPharmacyManager = this.authService.isPharmacyManager();
    this.isStockTransfer = this.authService.isStockTransfer();
    this.isAuditor = this.authService.isAuditor();
    this.isTaskReviewer = this.authService.isTaskReviewer();
    this.isStoreManager = this.authService.isStoreManager();
    this.isStoreApproval = this.authService.isStoreApproval();
    this.isMaximumDiscountOverride =
      this.authService.isMaximumDiscountOverride();
    const user = this.authService.getCurrentUser();
    this.currentClinic = this.authService.getCurrentClinic();
    if (!user) return;

    if (user.roles) {
      this.currentRole = this.authService.getCurrentUser()?.roles;
    }

    this.isaAdmin = user.isAdmin;
    this.userName = user.fullName;

    // Auto log-out subscription
    const timer$ = timer(2000, 5000);
    this.autoLogoutSubscription = timer$.subscribe(() => {
      void this.authGuard.canActivate();
    });
    this.autoFocusOnActiveOption();

    if (this.currentRole) {
      this.rolesWithoutDoctorAndPatient = this.currentRole.filter((item) => {
        if (item === Roles.Doctor || item === Roles.Patient) {
          return false;
        }
        return true;
      });
    }
    this.masterSubCategoryURLs = {
      medicineProfile: [
        '/masters/blood-group',
        '/masters/food-allergies',
        '/masters/other-allergies',
        '/masters/medicines',
        '/masters/medicine-type',
        '/masters/diseases',
        '/masters/procedures',
        '/masters/family-relations',
        '/masters/family-history-diseases',
        '/masters/symptoms',
      ],
      doctorProfile: [
        '/masters/specialization',
        '/masters/professional-qualification',
        '/masters/council',
        '/masters/category',
      ],
      appointment: ['/masters/test-reasons'],
      pharmacy: [
        '/masters/pharmacy/medicine-type',
        '/masters/pharmacy/medicine-brand',
        '/masters/pharmacy/medicine-category',
        '/masters/pharmacy/packing-type',
        '/masters/pharmacy/medicines',
        '/masters/pharmacy/distributors',
        '/masters/pharmacy/margin-slab',
      ],
      service: [
        '/masters/service-category',
        '/masters/service-subCategory',
        '/masters/service',
      ],
      process: [
        '/process/scheduler',
        '/process/list',
        '/process/assign-me',
        '/process/auditor',
        '/process/group',
      ],
      others: [
        '/masters/cancellation-reasons',
        '/masters/source-type',
        '/masters/primary-source',
        '/masters/secondary-source',
        '/masters/state',
        '/masters/city',
        '/masters/bill-category',
        '/masters/treatment',
        '/masters/process-category',
        '/masters/tpa',
        '/masters/payout-setting',
        '/masters/ipd-bill-setting',
        '/masters/add-expense-category',
      ],
      pharmacyModule: ['/pharmacy'],
      storeModule: ['/store'],
    };
  }

  getTooltip() {
    return this.rolesWithoutDoctorAndPatient
      .map((item) => UserType[item])
      .join(',');
  }

  getTooltipIfOverflow(element: HTMLElement): string {
    // Check if the content is overflowing
    if (element.scrollWidth > element.clientWidth) {
      return element.innerText || element.textContent || '';
    } else {
      return ''; // No tooltip if content is not overflowing
    }
  }

  autoFocusOnActiveOption() {
    const focusSubscription: Subscription = timer(0).subscribe((time) => {
      (
        document?.querySelector('a.mat-list-item.active') as HTMLElement
      )?.scrollIntoView({ block: 'center' });
      focusSubscription.unsubscribe();
    });
  }

  getClinicLocations() {
    const userId = this.authService.getCurrentUser()!.id;

    this.selectUserClinicService
      .getUserClinics(userId, this.authService.hasPharmacyManagerAccess())
      .subscribe((response) => {
        this.clinicLocations = response.centres;
        this.filteredClinicLocations = [...this.clinicLocations];
      });
  }

  openClinicLocationSelect() {
    this.clinicLocationSelect.open();
  }

  onClinicLocationSelect(event: MatSelectChange) {
    const clinic: Clinic = {
      name: event.source.value.name,
      id: event.source.value.id,
      hasPharmacy: event.source.value.hasPharmacy ?? false,
      sequentialNumber: event.source.value?.sequentialNumber,
      shortCode: event.source.value?.shortCode,
    };
    localStorage.setItem('currentClinic', JSON.stringify(clinic));
    location.reload();
  }

  isSelected(clinic: any): boolean {
    const currentClinic = JSON.parse(
      localStorage.getItem('currentClinic') || '{}'
    );
    return currentClinic && currentClinic.id === clinic.id;
  }

  getRoleCount(roleArray: number[]) {
    const len = roleArray.length - 1;
    if (len > 0) {
      return `+${len}`;
    } else {
      return '';
    }
  }

  getLoggedInUserRole() {
    if (this.isAdmin) {
      return `Admin ${this.getRoleCount(this.rolesWithoutDoctorAndPatient)}`;
    } else if (this.isClinicManager) {
      return `${this.userName} (Hospital Manager) ${this.getRoleCount(
        this.rolesWithoutDoctorAndPatient
      )}`;
    } else if (this.isReceptionist) {
      return `${this.userName} (Receptionist) ${this.getRoleCount(
        this.rolesWithoutDoctorAndPatient
      )}`;
    } else if (this.isPharmacyOperator) {
      return `${this.userName} (Pharmacy Approver) ${this.getRoleCount(
        this.rolesWithoutDoctorAndPatient
      )}`;
    } else if (this.isPharmacist) {
      return `${this.userName} (Pharmacist) ${this.getRoleCount(
        this.rolesWithoutDoctorAndPatient
      )}`;
    } else if (this.isPharmacyManager) {
      return `${this.userName} (Pharmacy Manager) ${this.getRoleCount(
        this.rolesWithoutDoctorAndPatient
      )}`;
    } else if (this.isStockTransfer) {
      return `${this.userName} (Stock Transfer) ${this.getRoleCount(
        this.rolesWithoutDoctorAndPatient
      )}`;
    } else if (this.isAuditor) {
      return `${this.userName} (Auditor) ${this.getRoleCount(
        this.rolesWithoutDoctorAndPatient
      )}`;
    } else if (this.isTaskReviewer) {
      return `${this.userName} (Task Reviewer) ${this.getRoleCount(
        this.rolesWithoutDoctorAndPatient
      )}`;
    } else if (this.isStoreManager) {
      return `${this.userName} (Store Manager) ${this.getRoleCount(
        this.rolesWithoutDoctorAndPatient
      )}`;
    } else if (this.isStoreApproval) {
      return `${this.userName} (Store Approval) ${this.getRoleCount(
        this.rolesWithoutDoctorAndPatient
      )}`;
    } else {
      return `${this.userName} (Operation Manager) ${this.getRoleCount(
        this.rolesWithoutDoctorAndPatient
      )}`;
    }
  }
  ngOnDestroy(): void {
    // tslint:disable-next-line: deprecation
    this.mobileQuery.removeListener(this._mobileQueryListener);
    this.autoLogoutSubscription.unsubscribe();
  }

  ngAfterViewInit(): void {
    this.changeDetectorRef.detectChanges();
  }

  isExpanded(subCategoryKey: string): boolean {
    const baseUrl = this._router.url;
    return this.masterSubCategoryURLs[subCategoryKey].some((url) =>
      baseUrl.startsWith(url)
    );
  }

  get approveMenuVisible(): boolean {
    return this.isPharmacyOperator || this.isOpsManager;
  }

  get pharmacyMenuVisible(): boolean {
    return (
      this.isPharmacist ||
      this.isPharmacyManager ||
      this.isStockTransfer ||
      this.isOpsManager ||
      this.isAdmin
    );
  }

  get isOnlyStoreApprovalRole(): boolean {
    const roles = this.rolesWithoutDoctorAndPatient || [];
    const hasHigherStoreAccess =
      this.isAdmin ||
      this.isOpsManager ||
      this.isClinicManager ||
      this.isStoreManager;

    return roles.includes(17) && !hasHigherStoreAccess;
  }
}
